import * as THREE from 'three/webgpu';
import {
    isSketchArcObject,
    isSketchCircleObject,
    isSketchEllipseObject,
    isSketchLineObject,
    isSketchParabolaObject,
    isSketchPointObject,
    isSketchSlotObject,
    isSketchSplineObject,
    isSketchStyleSplineObject,
} from '../../mesh/guards';
import {EllipseControlPointRole, type ISketchObject} from '../../mesh/sketch/ISketchObject';
import type {SketchPointObject} from '../../mesh/sketch/SketchPointObject';
import type {SketchSlotObject} from '../../mesh/sketch/SketchSlotObject';
import type {SketchSplineObject} from '../../mesh/sketch/SketchSplineObject';
import type {SketchStyleSplineObject} from '../../mesh/sketch/SketchStyleSplineObject';
import type {SketchEntityDefinitionSnapshot} from '../../../types/sketch/SketchEntityDefinitionTypes';

/**
 * 草图实体完整定义适配器。
 *
 * 职责与边界：
 * 1. 为属性编辑、约束求解、历史回滚提供同一套实体类型与控制点定义协议；
 * 2. 只接纳具备无损定义读写能力的实体，未知类型必须显式扩展，禁止退化为散点猜测；
 * 3. 每次按实体当前拓扑解析控制点，避免角点、修剪或样条重建后继续使用过期对象引用。
 *
 * 生命周期：服务或约束实例可长期持有；本类不创建渲染资源，无需释放。
 */
export class SketchEntityDefinitionAdapter {
    /** 完整定义复制复用向量。 */
    protected readonly definitionPointScratch = new THREE.Vector3();

    /**
     * 判断实体是否具备完整定义适配。
     * @param entity 待判断草图实体。
     * @returns 可以无损读写完整定义时返回 true。
     */
    public supports(entity: ISketchObject): boolean {
        return isSketchPointObject(entity)
            || isSketchLineObject(entity)
            || isSketchCircleObject(entity)
            || isSketchArcObject(entity)
            || isSketchEllipseObject(entity)
            || isSketchParabolaObject(entity)
            || isSketchSplineObject(entity)
            || isSketchStyleSplineObject(entity)
            || isSketchSlotObject(entity);
    }

    /**
     * 判断两个实体是否使用同一种完整定义协议。
     * @param left 第一实体。
     * @param right 第二实体。
     * @returns 两侧类型可一一配对时返回 true。
     */
    public supportsPair(left: ISketchObject, right: ISketchObject): boolean {
        return (isSketchPointObject(left) && isSketchPointObject(right))
            || (isSketchLineObject(left) && isSketchLineObject(right))
            || (isSketchCircleObject(left) && isSketchCircleObject(right))
            || (isSketchArcObject(left) && isSketchArcObject(right))
            || (isSketchEllipseObject(left) && isSketchEllipseObject(right)
                && this.haveCompatibleEllipseTopology(left, right))
            || (isSketchParabolaObject(left) && isSketchParabolaObject(right))
            || (isSketchSplineObject(left) && isSketchSplineObject(right)
                && this.haveCompatibleSplineTopology(left, right))
            || (isSketchStyleSplineObject(left) && isSketchStyleSplineObject(right)
                && this.haveCompatibleSplineTopology(left, right))
            || (isSketchSlotObject(left) && isSketchSlotObject(right)
                && left.isArcSlot() === right.isArcSlot());
    }

    /**
     * 判断控制点是否属于实体完整定义域。
     * @param entity 草图实体。
     * @param point 待判断控制点。
     * @returns 控制点参与实体定义时返回 true。
     */
    public ownsPoint(entity: ISketchObject, point: SketchPointObject): boolean {
        if (isSketchPointObject(entity)) {
            return entity === point;
        }
        return this.resolveControlPoints(entity).includes(point);
    }

    /**
     * 解析实体完整定义使用的稳定有序控制点域。
     * @param entity 草图实体。
     * @returns 与同类型实体一一配对的控制点数组。
     */
    public resolveControlPoints(entity: ISketchObject): SketchPointObject[] {
        let resolvedPoints: SketchPointObject[];
        if (isSketchPointObject(entity)) {
            resolvedPoints = [entity];
        } else if (isSketchLineObject(entity)) {
            resolvedPoints = [entity.startPoint, entity.endPoint];
        } else if (isSketchCircleObject(entity)) {
            resolvedPoints = [entity.getCenterControlPoint(), entity.getEdgeControlPoint()];
        } else if (isSketchArcObject(entity)) {
            resolvedPoints = [entity.getCenterPoint(), entity.getStartPoint(), entity.getEndPoint()];
        } else if (isSketchEllipseObject(entity)) {
            resolvedPoints = this.resolveEllipseControlPoints(entity);
        } else if (isSketchParabolaObject(entity)
            || isSketchSplineObject(entity)
            || isSketchStyleSplineObject(entity)) {
            resolvedPoints = entity.getControlPoints();
        } else if (isSketchSlotObject(entity)) {
            resolvedPoints = this.resolveSlotControlPoints(entity);
        } else {
            resolvedPoints = [];
        }

        return resolvedPoints;
    }

    /**
     * 把源实体完整定义原样写回同类型目标实体。
     * @param sourceEntity 权威定义来源。
     * @param targetEntity 待恢复实体。
     * @returns 类型匹配并完成写回时返回 true。
     */
    public copyDefinition(sourceEntity: ISketchObject, targetEntity: ISketchObject): boolean {
        if (!this.supportsPair(sourceEntity, targetEntity)) {
            return false;
        }
        if (isSketchPointObject(sourceEntity) && isSketchPointObject(targetEntity)) {
            sourceEntity.getPositionTo(this.definitionPointScratch);
            targetEntity.syncPositionSilently(this.definitionPointScratch);
            return true;
        }
        if (isSketchLineObject(sourceEntity) && isSketchLineObject(targetEntity)) {
            sourceEntity.startPoint.getPositionTo(this.definitionPointScratch);
            targetEntity.startPoint.syncPositionSilently(this.definitionPointScratch);
            sourceEntity.endPoint.getPositionTo(this.definitionPointScratch);
            targetEntity.endPoint.syncPositionSilently(this.definitionPointScratch);
            targetEntity.refreshGeometryOnly();
            return true;
        }
        if (isSketchCircleObject(sourceEntity) && isSketchCircleObject(targetEntity)) {
            sourceEntity.getCenterControlPoint().getPositionTo(this.definitionPointScratch);
            targetEntity.getCenterControlPoint().syncPositionSilently(this.definitionPointScratch);
            targetEntity.setRadiusSilently(sourceEntity.radius);
            return true;
        }
        if (isSketchArcObject(sourceEntity) && isSketchArcObject(targetEntity)) {
            targetEntity.updateArcDefinition(sourceEntity.getArcDefinition());
            targetEntity.syncControlPointsFromDefinition();
            return true;
        }
        if (isSketchEllipseObject(sourceEntity) && isSketchEllipseObject(targetEntity)) {
            targetEntity.updateEllipseDefinition(sourceEntity.getEllipseDefinition());
            targetEntity.syncControlPointsFromDefinition();
            return true;
        }
        if (isSketchParabolaObject(sourceEntity) && isSketchParabolaObject(targetEntity)) {
            targetEntity.updateParabolaDefinition(sourceEntity.getParabolaDefinition());
            return true;
        }
        if (isSketchSplineObject(sourceEntity) && isSketchSplineObject(targetEntity)) {
            return this.copySplineDefinition(sourceEntity, targetEntity);
        }
        if (isSketchStyleSplineObject(sourceEntity) && isSketchStyleSplineObject(targetEntity)) {
            return this.copySplineDefinition(sourceEntity, targetEntity);
        }
        if (isSketchSlotObject(sourceEntity) && isSketchSlotObject(targetEntity)) {
            return this.copySlotDefinition(sourceEntity, targetEntity);
        }
        return false;
    }

    /**
     * 把对象参数定义同步到其控制点域，不触发约束求解。
     *
     * 圆弧与椭圆的属性 setter 先更新参数定义，控制点仍可能停在旧位置；进入图求解前必须
     * 统一完成这一步，避免约束图读取“新参数 + 旧控制点”的中间态。其它实体 setter 已同步控制点。
     * @param entity 已完成参数写入的实体。
     */
    public synchronizeControlPointsFromDefinition(entity: ISketchObject): void {
        if (isSketchArcObject(entity) || isSketchEllipseObject(entity)) {
            entity.syncControlPointsFromDefinition();
        }
    }

    /**
     * 捕获不含 Object3D 或渲染资源的完整定义快照。
     * @param entity 草图实体。
     * @returns 支持实体的纯数据快照；未知实体返回 null。
     */
    public captureDefinition(entity: ISketchObject): SketchEntityDefinitionSnapshot | null {
        if (isSketchPointObject(entity)) {
            return {kind: 'point', position: entity.getPosition()};
        }
        if (isSketchLineObject(entity)) {
            return {kind: 'line', start: entity.startPoint.getPosition(), end: entity.endPoint.getPosition()};
        }
        if (isSketchCircleObject(entity)) {
            return {kind: 'circle', center: entity.getCenterWorld(), radius: entity.radius};
        }
        if (isSketchArcObject(entity)) {
            return {kind: 'arc', definition: entity.getArcDefinition()};
        }
        if (isSketchEllipseObject(entity)) {
            return {kind: 'ellipse', definition: entity.getEllipseDefinition()};
        }
        if (isSketchParabolaObject(entity)) {
            return {kind: 'parabola', definition: entity.getParabolaDefinition()};
        }
        if (isSketchSplineObject(entity) || isSketchStyleSplineObject(entity)) {
            return {
                kind: isSketchSplineObject(entity) ? 'spline' : 'style-spline',
                controlPointPositions: entity.getControlPoints().map((point) => point.getPosition()),
                curvePoints: entity.getCurvePoints(),
                curveMetadata: entity.getCurveMetadata(),
                curveEvaluationDefinition: entity.getCurveEvaluationDefinition(),
            };
        }
        if (isSketchSlotObject(entity)) {
            const edgeDefinitions = entity.getEdges().map((edge) => this.captureDefinition(edge));
            const constructionEntity = entity.isArcSlot()
                ? entity.constructionCenterlineArc
                : entity.constructionCenterline;
            const constructionDefinition = constructionEntity
                ? this.captureDefinition(constructionEntity)
                : null;
            if (edgeDefinitions.some((definition) => definition === null) || !constructionDefinition) {
                return null;
            }
            return {
                kind: 'slot',
                isArcSlot: entity.isArcSlot(),
                edgeDefinitions: edgeDefinitions as SketchEntityDefinitionSnapshot[],
                constructionDefinition,
                controlPointPositions: this.resolveControlPoints(entity).map((point) => point.getPosition()),
                geometryData: entity.geometryData,
            };
        }
        return null;
    }

    /**
     * 从纯数据快照恢复同类型实体完整定义。
     * @param entity 待恢复实体。
     * @param snapshot 权威定义快照。
     * @returns 类型匹配并恢复完成时返回 true。
     */
    public restoreDefinition(
        entity: ISketchObject,
        snapshot: SketchEntityDefinitionSnapshot,
    ): boolean {
        if (snapshot.kind === 'point' && isSketchPointObject(entity)) {
            entity.syncPositionSilently(snapshot.position);
            return true;
        }
        if (snapshot.kind === 'line' && isSketchLineObject(entity)) {
            entity.startPoint.syncPositionSilently(snapshot.start);
            entity.endPoint.syncPositionSilently(snapshot.end);
            entity.refreshGeometryOnly();
            return true;
        }
        if (snapshot.kind === 'circle' && isSketchCircleObject(entity)) {
            entity.getCenterControlPoint().syncPositionSilently(snapshot.center);
            entity.setRadiusSilently(snapshot.radius);
            return true;
        }
        if (snapshot.kind === 'arc' && isSketchArcObject(entity)) {
            entity.updateArcDefinition(snapshot.definition);
            entity.syncControlPointsFromDefinition();
            return true;
        }
        if (snapshot.kind === 'ellipse' && isSketchEllipseObject(entity)) {
            entity.updateEllipseDefinition(snapshot.definition);
            entity.syncControlPointsFromDefinition();
            return true;
        }
        if (snapshot.kind === 'parabola' && isSketchParabolaObject(entity)) {
            entity.updateParabolaDefinition(snapshot.definition);
            return true;
        }
        if ((snapshot.kind === 'spline' && isSketchSplineObject(entity))
            || (snapshot.kind === 'style-spline' && isSketchStyleSplineObject(entity))) {
            entity.rebuildByCurveDefinition({
                controlPointPositions: snapshot.controlPointPositions,
                curvePoints: snapshot.curvePoints,
                curveMetadata: snapshot.curveMetadata,
                curveEvaluationDefinition: snapshot.curveEvaluationDefinition ?? undefined,
            });
            return true;
        }
        if (snapshot.kind === 'slot' && isSketchSlotObject(entity)) {
            if (snapshot.isArcSlot !== entity.isArcSlot()) {
                return false;
            }
            const edges = entity.getEdges();
            if (edges.length !== snapshot.edgeDefinitions.length) {
                return false;
            }
            for (let index = 0; index < edges.length; index += 1) {
                if (!this.restoreDefinition(edges[index]!, snapshot.edgeDefinitions[index]!)) {
                    return false;
                }
            }
            const constructionEntity = entity.isArcSlot()
                ? entity.constructionCenterlineArc
                : entity.constructionCenterline;
            if (!constructionEntity
                || !this.restoreDefinition(constructionEntity, snapshot.constructionDefinition)) {
                return false;
            }
            const points = this.resolveControlPoints(entity);
            if (points.length !== snapshot.controlPointPositions.length) {
                return false;
            }
            points.forEach((point, index) => point.syncPositionSilently(snapshot.controlPointPositions[index]!));
            entity.geometryData = snapshot.geometryData;
            entity.constructionCenterline?.refreshGeometryOnly();
            // 圆弧中心线已由权威圆弧定义刷新；再次从控制点反解会引入回滚尾差。
            entity.refreshContourGeometry();
            return true;
        }
        return false;
    }

    /**
     * 判定两个椭圆是否具有可一一映射的完整/部分区间与控制点角色域。
     * @param left 第一椭圆。
     * @param right 第二椭圆。
     * @returns 区间类型与已注册角色完全一致时返回 true。
     */
    private haveCompatibleEllipseTopology(
        left: ISketchObject,
        right: ISketchObject,
    ): boolean {
        if (!isSketchEllipseObject(left) || !isSketchEllipseObject(right)) {
            return false;
        }
        if (left.canCreateContour() !== right.canCreateContour()) {
            return false;
        }
        const leftRoles = [...left.getCardinalControlPointsSnapshot().keys()].sort();
        const rightRoles = [...right.getCardinalControlPointsSnapshot().keys()].sort();
        return leftRoles.length === rightRoles.length
            && leftRoles.every((role, index) => role === rightRoles[index]);
    }

    /**
     * 判定两个同类样条是否共享相同控制点域与曲线拓扑元数据。
     * @param left 第一条样条。
     * @param right 第二条样条。
     * @returns 控制点可一一对应且闭合、曲线种类、阶次和权重域一致时返回 true。
     */
    private haveCompatibleSplineTopology(
        left: SketchSplineObject | SketchStyleSplineObject,
        right: SketchSplineObject | SketchStyleSplineObject,
    ): boolean {
        const leftPoints = left.getControlPoints();
        const rightPoints = right.getControlPoints();
        if (leftPoints.length !== rightPoints.length) {
            return false;
        }
        const leftMetadata = left.getCurveMetadata();
        const rightMetadata = right.getCurveMetadata();
        return leftMetadata.selectionKind === rightMetadata.selectionKind
            && leftMetadata.curveKind === rightMetadata.curveKind
            && leftMetadata.closureKind === rightMetadata.closureKind
            && leftMetadata.styleSplineMode === rightMetadata.styleSplineMode
            && leftMetadata.degree === rightMetadata.degree
            && leftMetadata.weights.length === rightMetadata.weights.length;
    }

    /**
     * 按稳定角色顺序解析椭圆控制点。
     * @param ellipse 椭圆实体。
     * @returns 椭圆完整控制点域。
     */
    private resolveEllipseControlPoints(entity: ISketchObject): SketchPointObject[] {
        if (!isSketchEllipseObject(entity)) {
            return [];
        }
        const cardinalPoints = entity.getCardinalControlPointsSnapshot();
        const points = [entity.getCenterPoint(), entity.getStartPoint(), entity.getEndPoint()];
        [
            EllipseControlPointRole.MajorPositive,
            EllipseControlPointRole.MajorNegative,
            EllipseControlPointRole.MinorPositive,
            EllipseControlPointRole.MinorNegative,
        ].forEach((role) => {
            const cardinalPoint = cardinalPoints.get(role);
            if (cardinalPoint && !points.includes(cardinalPoint)) {
                points.push(cardinalPoint);
            }
        });
        return points;
    }

    /**
     * 原样恢复样条完整定义。
     * @param sourceEntity 权威样条。
     * @param targetEntity 待恢复样条。
     * @returns 恢复完成时返回 true。
     */
    private copySplineDefinition(
        sourceEntity: SketchSplineObject | SketchStyleSplineObject,
        targetEntity: SketchSplineObject | SketchStyleSplineObject,
    ): boolean {
        targetEntity.rebuildByCurveDefinition({
            controlPointPositions: sourceEntity.getControlPoints().map((point) => point.getPosition()),
            curvePoints: sourceEntity.getCurvePoints(),
            curveMetadata: sourceEntity.getCurveMetadata(),
            curveEvaluationDefinition: sourceEntity.getCurveEvaluationDefinition() ?? undefined,
        });
        return true;
    }

    /**
     * 原样恢复槽口及派生控制点。
     * @param sourceEntity 权威槽口。
     * @param targetEntity 待恢复槽口。
     * @returns 模式和内部定义一致时返回 true。
     */
    private copySlotDefinition(sourceEntity: SketchSlotObject, targetEntity: SketchSlotObject): boolean {
        const sourceEdges = sourceEntity.getEdges();
        const targetEdges = targetEntity.getEdges();
        for (let index = 0; index < sourceEdges.length; index += 1) {
            const sourceEdge = sourceEdges[index];
            const targetEdge = targetEdges[index];
            if (!sourceEdge || !targetEdge || !this.copyDefinition(sourceEdge, targetEdge)) {
                return false;
            }
        }
        const sourceConstruction = sourceEntity.isArcSlot()
            ? sourceEntity.constructionCenterlineArc
            : sourceEntity.constructionCenterline;
        const targetConstruction = targetEntity.isArcSlot()
            ? targetEntity.constructionCenterlineArc
            : targetEntity.constructionCenterline;
        if (!sourceConstruction || !targetConstruction
            || !this.copyDefinition(sourceConstruction, targetConstruction)) {
            return false;
        }
        const sourcePoints = this.resolveControlPoints(sourceEntity);
        const targetPoints = this.resolveControlPoints(targetEntity);
        if (sourcePoints.length !== targetPoints.length) {
            return false;
        }
        sourcePoints.forEach((point, index) => {
            point.getPositionTo(this.definitionPointScratch);
            targetPoints[index]!.syncPositionSilently(this.definitionPointScratch);
        });
        targetEntity.constructionCenterline?.refreshGeometryOnly();
        // 圆弧中心线已由 copyDefinition 原子刷新，禁止再从控制点反推覆盖权威定义。
        targetEntity.refreshContourGeometry();
        return true;
    }

    /**
     * 收集槽口全部定义控制点并按对象身份去重。
     * @param slot 槽口实体。
     * @returns 生命周期内稳定的有序控制点域。
     */
    private resolveSlotControlPoints(slot: SketchSlotObject): SketchPointObject[] {
        const points: SketchPointObject[] = [];
        const appendUnique = (point: SketchPointObject | null): void => {
            if (point && !points.includes(point)) {
                points.push(point);
            }
        };
        slot.getEdges().forEach((edge) => {
            this.resolveControlPoints(edge).forEach(appendUnique);
        });
        const constructionEntity = slot.isArcSlot()
            ? slot.constructionCenterlineArc
            : slot.constructionCenterline;
        if (constructionEntity) {
            this.resolveControlPoints(constructionEntity).forEach(appendUnique);
        }
        slot.getJunctionPointArray().forEach(appendUnique);
        appendUnique(slot.centerlineStartPoint);
        appendUnique(slot.centerlineEndPoint);
        appendUnique(slot.centerPoint);
        appendUnique(slot.arcCenterPoint);
        appendUnique(slot.centerArcMidpoint);
        return points;
    }
}
